/**
 * 🪙  useGA4 — minimal, no-3rd-party-package GA4 loader.
 *
 * Behavior:
 *   ❶ Loads gtag.js lazily, only AFTER the cookie consent banner has
 *      been positively acknowledged (`localStorage.life_cookie_consent === "accepted"`).
 *   ❷ Reads the measurement ID from `process.env.REACT_APP_GA4_ID`.
 *      Defaults to a `G-PLACEHOLDER` sentinel so the loader can be
 *      validated in dev without ever talking to Google.
 *   ❸ Fires a `page_view` on every route change.
 *
 * If the ID is the placeholder, we still wire `window.dataLayer.push`
 * calls so the integration is testable; the actual `<script>` tag is
 * suppressed to avoid 404 chatter against an invalid measurement ID.
 */
import { useEffect } from "react";

const GA_ID         = process.env.REACT_APP_GA4_ID || "G-PLACEHOLDER";
const GA_IS_REAL    = /^G-[A-Z0-9]{6,}$/.test(GA_ID) && GA_ID !== "G-PLACEHOLDER";
const CONSENT_KEY   = "life_cookie_consent";

function _ensureDataLayer() {
  if (typeof window === "undefined") return;
  window.dataLayer = window.dataLayer || [];
  if (!window.gtag) {
    window.gtag = function gtag() { window.dataLayer.push(arguments); };
  }
}

function _injectGtagScript() {
  if (typeof document === "undefined") return;
  if (document.getElementById("ga4-loader")) return;
  if (!GA_IS_REAL) return; // never inject against the placeholder
  const s = document.createElement("script");
  s.id = "ga4-loader";
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(GA_ID)}`;
  document.head.appendChild(s);

  _ensureDataLayer();
  window.gtag("js", new Date());
  window.gtag("config", GA_ID, { send_page_view: false });
}

function _consented() {
  try { return localStorage.getItem(CONSENT_KEY) === "accepted"; }
  catch { return false; }
}

export function useGA4(pathname) {
  useEffect(() => {
    if (!_consented()) return;
    _ensureDataLayer();
    _injectGtagScript();
    if (typeof window === "undefined" || !window.gtag) return;
    window.gtag("event", "page_view", {
      page_path:      pathname,
      page_location:  window.location.href,
      ga_id_status:   GA_IS_REAL ? "live" : "placeholder",
    });
  }, [pathname]);
}

export function ga4Event(name, params = {}) {
  if (!_consented()) return;
  _ensureDataLayer();
  if (typeof window === "undefined" || !window.gtag) return;
  window.gtag("event", name, params);
}

export const GA4_STATUS = { id: GA_ID, isReal: GA_IS_REAL };
